'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';

import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import useNewGetPayerMix from 'src/hooks/payerMix/useNewGetPayerMix';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { getProfileUrl } from 'src/utils/profileUrl';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';

const PAYER_COLOR_MAP: Record<string, string> = {
  Commercial: '#f97316',
  Medicaid: '#f43f5e',
  'Medicare Advantage': '#6366f1',
  'Medicare FFS': '#3739df',
  Other: '#06b6d4',
  'Other Government': '#06b6d4',
  Tricare: '#10b981',
  VA: '#1e40af',
};

const DEFAULT_PAYER_COUNT = 3;

const CompactPayerMix: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();
  const { closeDrawer } = useDrawer();

  const { type } = useParams<{ type: string }>();
  const { providerId } = getEntityIdentifiers(entityDetails);
  const profileType = entityDetails?.type || type || 'hcp';
  const isSystem = profileType === 'system';
  const filterSetId = widgetConfig?.filterSet?.id;
  const codeSetLabel = widgetConfig?.filterSet?.label
    ? `${widgetConfig.filterSet.label} Claims`
    : 'All Claims';

  const { data: payerMixByType, isLoading: isTypeLoading } = useNewGetPayerMix({
    providerId,
    groupBy: 'payer_type',
    location: profileType,
    filterSetId,
  });

  const { data: payerMixByName, isLoading: isNameLoading } = useNewGetPayerMix({
    providerId,
    groupBy: 'payer_name',
    location: profileType,
    filterSetId,
  });

  const isLoading = isTypeLoading || isNameLoading;

  const payerTypeData = useMemo(() => {
    if (!payerMixByType?.results) return [];
    return payerMixByType.results.map(
      (item: { payer_type: string; percentage: number }) => ({
        name: item.payer_type,
        value: item.percentage,
      })
    );
  }, [payerMixByType]);

  const sortedPayerNameData = useMemo(() => {
    if (!payerMixByName?.results) return [];
    return [...payerMixByName.results].sort(
      (a: Record<string, any>, b: Record<string, any>) =>
        b.percentage - a.percentage
    );
  }, [payerMixByName]);

  const isICD = useMemo(() => {
    return widgetConfig?.filterSet?.detailed_filters?.some(
      (filter: { type: string }) => filter.type.toLowerCase().includes('icd')
    );
  }, [widgetConfig?.filterSet?.detailed_filters]);

  const displayedPayers = sortedPayerNameData.slice(0, DEFAULT_PAYER_COUNT);
  const hasMoreThanDefault = sortedPayerNameData.length > DEFAULT_PAYER_COUNT;

  return (
    <Card
      className="p-4 flex flex-col gap-2 bg-white h-full"
      data-testid="compact-payer-mix"
    >
      <WidgetHeader fallbackTitle={`Payer Mix - ${codeSetLabel}`} />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading || widgetConfig?.isLoading ? (
          <WidgetLoading data-testid="compact-payer-mix-loading" />
        ) : payerTypeData.length === 0 && isICD ? (
          <WidgetEmpty
            data-testid="compact-payer-mix-empty"
            variant="full"
            message="Payer mix is currently unavailable for diagnosis codes. We are working on adding support for this soon."
          />
        ) : payerTypeData.length === 0 ? (
          <WidgetEmpty data-testid="compact-payer-mix-empty" variant="full" />
        ) : (
          <div
            className="flex flex-col gap-4"
            data-testid="compact-payer-mix-content"
          >
            {/* Stacked bar */}
            <div
              className="h-3 rounded-full overflow-hidden flex"
              data-testid="compact-payer-mix-bar"
            >
              {payerTypeData.map((item) => (
                <div
                  key={item.name}
                  className="h-full"
                  data-testid={`payer-segment-${item.name}`}
                  style={{
                    flex: item.value,
                    backgroundColor: PAYER_COLOR_MAP[item.name] || '#94a3b8',
                  }}
                />
              ))}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {payerTypeData.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{
                      backgroundColor: PAYER_COLOR_MAP[item.name] || '#94a3b8',
                    }}
                  />
                  <span className="text-xs text-neutral-600">
                    {item.name} {item.value}%
                  </span>
                </div>
              ))}
            </div>

            {/* Top Payers by Name */}
            {sortedPayerNameData.length > 0 && (
              <div
                className="flex flex-col gap-2"
                data-testid="compact-payer-mix-table"
              >
                <span className="text-sm font-bold text-primary">
                  Top Payers by Name
                </span>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-neutral-600">
                    Payer
                  </span>
                  <span className="text-xs font-bold text-neutral-600">
                    % of Total
                  </span>
                </div>
                {displayedPayers.map(
                  (payer: Record<string, any>, index: number) => (
                    <div
                      key={`${payer.payer_name}-${index}`}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm text-neutral-800">
                        {payer.payer_name}
                      </span>
                      <span className="text-sm text-neutral-800">
                        {Math.round(payer.percentage)}%
                      </span>
                    </div>
                  )
                )}
                {hasMoreThanDefault && !isSystem && (
                  <Link
                    href={getProfileUrl(profileType, providerId, 'payer-mix')}
                    onClick={() => closeDrawer('profile')}
                    className="flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-700 transition-colors"
                    data-testid="compact-payer-mix-view-all"
                  >
                    View all payers
                    <Icon icon={faArrowRight} className="h-3 w-3" />
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default CompactPayerMix;
